/**
 * Remotion SVG Overlay Component
 *
 * Renders SvgOverlay from Post.ts using the provided viewBox.
 * Supports fill color override (solid or gradient), stroke override, opacity, rotation.
 */

import React, { useId, useMemo } from 'react';
import { SvgOverlay, GradientConfig, isGradientConfig } from '../../models/Post';
import { namespaceSvgIds } from "../../lib/svgIds";

interface RemotionSvgOverlayProps {
  overlay: SvgOverlay;
  containerWidth: number;
  containerHeight: number;
  /** Scale factor: containerWidth / referenceMediaWidth */
  scaleFactor?: number;
  /** Layout engine: 'fabric' (old posts) applies rotation compensation */
  layoutEngine?: 'fabric' | 'remotion';
}

/**
 * Convert GradientConfig to SVG gradient definition
 */
function gradientToSvgDef(gradient: GradientConfig, id: string): string {
  const stops = gradient.colorStops
    .map((stop) => {
      const opacityAttr = stop.opacity !== undefined ? ` stop-opacity="${stop.opacity}"` : '';
      return `<stop offset="${stop.offset * 100}%" stop-color="${stop.color}"${opacityAttr} />`;
    })
    .join('');

  if (gradient.type === 'linear') {
    const angle = gradient.angle ?? 0;
    const angleRad = ((angle - 90) * Math.PI) / 180;
    const x1 = 50 - Math.cos(angleRad) * 50;
    const y1 = 50 - Math.sin(angleRad) * 50;
    const x2 = 50 + Math.cos(angleRad) * 50;
    const y2 = 50 + Math.sin(angleRad) * 50;

    return `<linearGradient id="${id}" x1="${x1}%" y1="${y1}%" x2="${x2}%" y2="${y2}%">${stops}</linearGradient>`;
  } else {
    return `<radialGradient id="${id}" cx="50%" cy="50%" r="50%">${stops}</radialGradient>`;
  }
}


/**
 * Process SVG content to apply color overrides.
 * - If fillColor is set but no strokeColor: stroke gets fillColor (or gradient URL for gradients)
 * - If strokeColor is explicitly set: use that
 * - strokeWidth only applies if explicitly set
 */
function processSvgContent(
  svgContent: string,
  fillColor: string | GradientConfig | undefined,
  strokeColor: string | undefined,
  strokeWidth: number | undefined,
  gradientId: string
): { processedContent: string; gradientDef: string | null } {
  let processedContent = svgContent;
  let gradientDef: string | null = null;

  if (fillColor !== undefined) {
    if (isGradientConfig(fillColor)) {
      gradientDef = gradientToSvgDef(fillColor, gradientId);
      processedContent = processedContent.replace(/fill="(?!none")[^"]*"/g, `fill="url(#${gradientId})"`);
      processedContent = processedContent.replace(/fill:\s*(?!none)[^;"}]+/g, `fill: url(#${gradientId})`);

      if (strokeColor === undefined) {
        processedContent = processedContent.replace(/stroke="[^"]*"/g, `stroke="url(#${gradientId})"`);
        processedContent = processedContent.replace(/stroke:\s*[^;"}]+/g, `stroke: url(#${gradientId})`);
      }
    } else {
      processedContent = processedContent.replace(/fill="(?!none")[^"]*"/g, `fill="${fillColor}"`);
      processedContent = processedContent.replace(/fill:\s*(?!none)[^;"}]+/g, `fill: ${fillColor}`);

      if (strokeColor === undefined) {
        processedContent = processedContent.replace(/stroke="[^"]*"/g, `stroke="${fillColor}"`);
        processedContent = processedContent.replace(/stroke:\s*[^;"}]+/g, `stroke: ${fillColor}`);
      }
    }
  }

  if (strokeColor !== undefined) {
    processedContent = processedContent.replace(/stroke="[^"]*"/g, `stroke="${strokeColor}"`);
    processedContent = processedContent.replace(/stroke:\s*[^;"}]+/g, `stroke: ${strokeColor}`);
  }

  if (strokeWidth !== undefined) {
    processedContent = processedContent.replace(/stroke-width="[^"]*"/g, `stroke-width="${strokeWidth}"`);
    processedContent = processedContent.replace(/stroke-width:\s*[^;"}]+/g, `stroke-width: ${strokeWidth}`);
  }

  return { processedContent, gradientDef };
}

export const RemotionSvgOverlay: React.FC<RemotionSvgOverlayProps> = ({
  overlay,
  containerWidth,
  containerHeight,
  scaleFactor = 1,
  layoutEngine,
}) => {
  const {
    id,
    svgContent,
    viewBox,
    position,
    width,
    height,
    opacity = 100,
    rotation = 0,
    zIndex = 0,
    fillColor,
    strokeColor,
    strokeWidth,
  } = overlay;

  const viewBoxParts = viewBox.trim().split(/\s+/).map(Number);
  const vbWidth = viewBoxParts[2] || 100;
  const vbHeight = viewBoxParts[3] || 100;

  // Calculate pixel dimensions and position from percentages
  const left = (position.x / 100) * containerWidth;
  const top = (position.y / 100) * containerHeight;
  const widthPx = (width / 100) * containerWidth;

  let heightPx: number;
  if (height !== undefined && height > 0) {
    heightPx = (height / 100) * containerHeight;
  } else {
    // Use viewBox aspect ratio for fallback
    heightPx = widthPx * (vbHeight / vbWidth);
  }

  let rotationTransform: string | undefined;
  if (rotation) {
    if (layoutEngine !== 'remotion') {
      const rad = (rotation * Math.PI) / 180;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);
      const dx = (widthPx / 2) * (cos - 1) - (heightPx / 2) * sin;
      const dy = (widthPx / 2) * sin + (heightPx / 2) * (cos - 1);
      rotationTransform = `translate(${dx}px, ${dy}px) rotate(${rotation}deg)`;
    } else {
      rotationTransform = `rotate(${rotation}deg)`;
    }
  }

  // Use React's useId() to make gradient IDs unique per component instance,
  // preventing SVG <defs> ID collisions across multiple posts on the same page.
  const instanceId = useId();
  const gradientId = `gradient-${id}-${instanceId.replace(/:/g, '')}`;
  const { processedContent, gradientDef } = useMemo(
    () => processSvgContent(namespaceSvgIds(svgContent, instanceId.replace(/:/g, '')), fillColor, strokeColor, strokeWidth, gradientId),
    [svgContent, fillColor, strokeColor, strokeWidth, gradientId, instanceId]
  );

  // Build SVG using provided viewBox.
  // If content uses __W__/__H__ placeholders, use a pixel-matching viewBox so
  // features like corner radii don't distort under non-uniform scaling.
  const fullSvg = useMemo(() => {
    const defs = gradientDef ? `<defs>${gradientDef}</defs>` : '';
    const hasDynamicSize = processedContent.includes('__W__') || processedContent.includes('__H__') || processedContent.includes('__R__');
    let resolvedContent = processedContent;
    if (hasDynamicSize) {
      // Scale hardcoded numeric rx/ry from viewBox units to pixel units BEFORE
      // substituting __R__ (which is non-numeric text and won't match the regex).
      const scaleX = widthPx / vbWidth;
      const scaleY = heightPx / vbHeight;
      // Use the same scale factor for both rx and ry to keep corners circular
      const radiusScale = Math.min(scaleX, scaleY);
      resolvedContent = resolvedContent.replace(/rx="(\d+(?:\.\d+)?)"/g, (_, val) => `rx="${Number(val) * radiusScale}"`);
      resolvedContent = resolvedContent.replace(/ry="(\d+(?:\.\d+)?)"/g, (_, val) => `ry="${Number(val) * radiusScale}"`);
      // Now substitute dimension placeholders
      resolvedContent = resolvedContent.replace(/__W__/g, String(widthPx)).replace(/__H__/g, String(heightPx)).replace(/__R__/g, String(Math.min(widthPx, heightPx) / 2));
    }
    const resolvedViewBox = hasDynamicSize ? `0 0 ${widthPx} ${heightPx}` : viewBox;
    const par = hasDynamicSize ? 'xMidYMid meet' : 'none';
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${resolvedViewBox}" width="${widthPx}" height="${heightPx}" preserveAspectRatio="${par}" overflow="visible" style="display:block;">${defs}${resolvedContent}</svg>`;
  }, [gradientDef, processedContent, widthPx, heightPx, viewBox, vbWidth, vbHeight]);

  return (
    <div
      style={{
        position: 'absolute',
        left,
        top,
        width: widthPx,
        height: heightPx,
        transform: rotationTransform,
        transformOrigin: 'center',
        zIndex,
        opacity: opacity / 100,
        overflow: 'visible',
      }}
      dangerouslySetInnerHTML={{ __html: fullSvg }}
    />
  );
};

export default RemotionSvgOverlay;
